import React from 'react';
import styled from '@emotion/styled';
import { __ } from '../../helpers/TranslationService';
import { ICabinInfo, IFormattedCabin, IFormattedCabins, IRate, ISail } from '../../interfaces';

import { RadioOnIcon } from '../Icons/newIcons/RadioOnIcon';
import { RadioOffIcon } from '../Icons/newIcons/RadioOffIcon';
import { IExchangeRate } from '../../interfaces/IHeader';
import { getCurrencyString } from '../PriceString';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import {
    CabinCard,
    CabinCardDetails,
    CabinCardDetailsWrapper,
    CabinContainer,
    CabinImageWrapper,
    CabinTypeDiscount,
    CabinTypePrice,
    CabinTypeTitle,
    StyledCabinImage,
} from './CruiseInquiryCabinSelection.style';
import { useDispatch } from 'react-redux';
import { eventTracker } from '../../helpers/EventTracker/EventTracker';
import { CruisePageActionCreators } from '../../reducers/cruisePage/action-creators';
import { updateRequestFormFields } from '../../consts/actionTypes';
import { SectionContainer, SectionHeader } from './CruiseInquiryForm.style';

interface ICabinItem {
    discountThreshold: number;
    typeName: string;
    cabinType: IFormattedCabin;
    selectedCabinId?: number;
    isAllInclusive?: boolean;
    selectedRates?: IRate[];
    cabins: IFormattedCabins;
    exchangeRate: IExchangeRate;
    onClick: (type: string, id: number) => void;
}

const CabinItem = ({
    selectedRates,
    cabinType,
    cabins,
    typeName,
    selectedCabinId,
    isAllInclusive,
    exchangeRate,
    discountThreshold,
    onClick,
}: ICabinItem) => {
    const { image, cabinNid } = cabinType;
    const isDualCurrency = exchangeRate !== null;

    const referenceCabin: ICabinInfo = cabins[typeName].cabins.find(
        (cabin) => cabin.cabinType.nid === cabinNid,
    );

    const isCabinSelected = cabinType.cabins.some(
        (cabin) => cabin.cabinType.nid === selectedCabinId,
    );
    const cabinRates = selectedRates?.find((e) => e.cabintype_id === referenceCabin.cabinType.nid);
    const allInPrice = cabinRates?.all_in_price?.per_person_price;
    const isAllInPrice = !!allInPrice;

    return (
        <CabinCard onClick={() => onClick(typeName, cabinNid)}>
            <CabinImageWrapper isCabinSelected={isCabinSelected}>
                <StyledCabinImage
                    imageUrl={image.original}
                    alt={__(typeName, 'dreamlines')}
                    imageFallback="cabin"
                />
            </CabinImageWrapper>

            <CabinCardDetailsWrapper isCabinSelected={isCabinSelected}>
                {isCabinSelected ? (
                    <RadioOnIcon
                        style={{
                            fill: 'white',
                        }}
                    />
                ) : (
                    <RadioOffIcon
                        style={{
                            fill: 'white',
                        }}
                    />
                )}

                <CabinCardDetails>
                    <CabinTypeTitle>
                        {__(typeName, 'dreamlines')}
                        {referenceCabin.discountPercentage >= discountThreshold && (
                            <CabinTypeDiscount>
                                -{referenceCabin.discountPercentage}%
                            </CabinTypeDiscount>
                        )}
                    </CabinTypeTitle>

                    {!isAllInclusive || !isCabinSelected || !isAllInPrice ? (
                        <CabinTypePrice>
                            {!isDualCurrency
                                ? referenceCabin.cabinType.labels.priceTable.labelMainCurrency
                                : referenceCabin.cabinType.labels.priceTable.labelDualCurrency}
                        </CabinTypePrice>
                    ) : (
                        <CabinTypePrice
                            hasDiscount={referenceCabin.discountPercentage >= discountThreshold}
                        >
                            {` ${__('from', 'dreamlines')}`}
                            {getCurrencyString(allInPrice, referenceCabin.currency)}
                            {` ${__('p.P.', 'dreamlines')}`}
                        </CabinTypePrice>
                    )}
                </CabinCardDetails>
            </CabinCardDetailsWrapper>
        </CabinCard>
    );
};

interface IRequestFormCabins {
    exchangeRate?: IExchangeRate;
    cabins: IFormattedCabins;
    selectedCabinId?: number;
    selectedRates?: IRate[];
    sails: ISail[];
}

export const CruiseInquiryCabinSelection = ({
    cabins,
    exchangeRate,
    selectedCabinId,
    selectedRates,
    sails,
}: IRequestFormCabins) => {
    const cabinTypes = Object.entries(cabins || {}).filter(
        ([key]) => cabins[key].cabins?.length > 0,
    );

    const dispatch = useDispatch();
    const { pageData } = useTypedSelector((state) => state);

    const handleCabinChange = (typeName: string, id: number): void => {
        if (!sails.length) {
            return;
        }
        eventTracker.track({
            category: 'cruisepage-selected cabin',
            action: `${typeName === 'suite' ? `${typeName}-picture` : `${typeName} cabin-picture`}`,
            label: ' ',
        });
        dispatch(CruisePageActionCreators.setCabin(id));
        dispatch({
            type: updateRequestFormFields,
            payload: { cabinNid: id },
        });
    };

    return (
        <SectionContainer>
            <SectionHeader>{__('Cabin', 'dreamlines')}</SectionHeader>

            <CabinContainer>
                {cabinTypes.map(([typeName, cabinType]) => {
                    return (
                        <CabinItem
                            key={typeName}
                            cabinType={cabinType}
                            typeName={typeName}
                            exchangeRate={exchangeRate}
                            cabins={cabins}
                            selectedCabinId={selectedCabinId}
                            selectedRates={selectedRates}
                            discountThreshold={pageData.discountThreshold}
                            onClick={handleCabinChange}
                        />
                    );
                })}
            </CabinContainer>
        </SectionContainer>
    );
};
